import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { Application, ApplicationsService } from '@nrwl/common-lib';
import { takeUntil } from 'rxjs/operators';

interface INoData {
  noValue: boolean;
}

@Component({
  selector: 'nrwl-apps-page',
  templateUrl: './applications.page.template.html',
  styleUrls: ['./applications.page.style.scss']
})
export class ApplicationsPageComponent implements OnInit, OnDestroy {
  @Input() refreshApplications?: BehaviorSubject<boolean>;
  widgetConfig: any = {}; //eslint-disable-line @typescript-eslint/no-explicit-any
  applicationData: Array<Application>;
  cacheObj: Subscription;
  waitSubscription: Subscription;
  output: Subject<any>; //eslint-disable-line @typescript-eslint/no-explicit-any
  label: string;
  showLabel: boolean;
  dataSource: BehaviorSubject<Application[] | INoData>;
  authorized = false;
  isLoading = true;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(readonly applicationsService: ApplicationsService) {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.dataSource = new BehaviorSubject<any>({ noValue: true });
  }

  ngOnInit(): void {
    this.showLabel = true;
    this.authorized = this.applicationsService.authorized;
    // do not subscribe to cache immediately - first call service and wait until you are sure
    // that first shot of data has arrived. Otherwise your subscription to cache will fire twice:
    // first - with original value of cache (which is []) - and only then with newly arrived apps.
    // For me that would be a small issue, but QA+automated tests are unhappy - they notice the loader widget is down,
    // they check html - we show 'no data' - and then their tests fail.

    //TODO(Cristian): subscribe in subscribe is never a good idea
    // => this must be rewrite with proper operators
    this.waitSubscription = this.applicationsService.getValuesAsync().subscribe(() => {
      // we can ignore the outcome - we shall subscribe straight to cache subject (and we know it is populated now)
      this.cacheObj = this.applicationsService.cache.subscribe(data => {
        this.isLoading = false;
        this.applicationData = this.applicationsService.getUserDefinedAppsOnly(data);
        this.dataSourceChanged(this.applicationData);
      });
    });
    this.cacheObj = this.refreshApplications.subscribe(refresh => {
      if (refresh) {
        this.refreshApplicationList();
      }
    });
  }

  refreshApplicationList(): void {
    this.isLoading = true;
    this.applicationsService
      .updateApplication(this.applicationsService.getApplications())
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        response => {
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  ngOnDestroy(): void {
    if (this.waitSubscription) {
      this.waitSubscription.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
  }

  dataSourceChanged(dataSource: Application[]): void {
    this.dataSource.next(dataSource);
  }
}
